import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Star, TrendingUp, RefreshCw, Edit, History, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const GRADES = [
  { value: 'Nouveau', color: 'bg-gray-500/20 text-gray-400', description: 'Aucune transaction' },
  { value: 'Débutant', color: 'bg-primary/20 text-primary', description: '1+ transactions' },
  { value: 'Fiable', color: 'bg-green-500/20 text-green-400', description: '5+ trans, 4.0+ rating' },
  { value: 'Premium', color: 'bg-violet-500/20 text-violet-400', description: '20+ trans, 4.5+ rating' },
  { value: 'Elite', color: 'bg-yellow-500/20 text-yellow-400', description: '50+ trans, 4.8+ rating' },
  { value: 'Suspendu', color: 'bg-red-500/20 text-red-400', description: 'Compte suspendu' }
];

const UserScoreManager = ({ user, token, API, onUpdate }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    grade: user.grade || 'Nouveau',
    trust_score: user.trust_score || 50,
    seller_rating: user.seller_rating || 0,
    buyer_rating: user.buyer_rating || 0,
    reason: ''
  });

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/${user.id}/recalculate-score`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scores recalculés automatiquement');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du recalcul');
    } finally {
      setLoading(false);
    }
  };

  const handleManualUpdate = async () => {
    if (!formData.reason || formData.reason.trim().length < 10) {
      toast.error('Veuillez fournir une raison (min 10 caractères)');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/users/${user.id}/score`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Score mis à jour manuellement');
      setEditDialogOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/users/${user.id}/score-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScoreHistory(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    }
  };

  const getGradeInfo = (grade) => GRADES.find(g => g.value === grade) || GRADES[0];
  const gradeInfo = getGradeInfo(user.grade);

  return (
    <div className="space-y-4">
      {/* Current Scores Display */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Grade</p>
          <Badge className={gradeInfo.color}>{user.grade || 'Nouveau'}</Badge>
          {user.score_manually_set && (
            <span className="ml-2 text-xs text-amber-500" title="Modifié manuellement">⚠️</span>
          )}
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Score de confiance</p>
          <p className="font-bold text-lg">{user.trust_score?.toFixed(0) || 50}/100</p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Note vendeur</p>
          <p className="font-bold flex items-center gap-1">
            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
            {user.seller_rating?.toFixed(1) || '0.0'}
          </p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Note acheteur</p>
          <p className="font-bold flex items-center gap-1">
            <Star className="w-4 h-4 fill-primary text-primary" />
            {user.buyer_rating?.toFixed(1) || '0.0'}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm text-muted-foreground">
        <span>{user.total_sales || 0} ventes</span>
        <span>{user.total_purchases || 0} achats</span>
        <span>{user.disputes || 0} litiges</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRecalculate}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Recalculer
        </Button>

        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Edit className="w-4 h-4 mr-1" />
              Modifier manuellement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" />
                Modifier le score de {user.first_name}
              </DialogTitle>
              <DialogDescription>
                Cette modification sera enregistrée dans l'historique.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Grade</Label>
                <Select
                  value={formData.grade}
                  onValueChange={(v) => setFormData({ ...formData, grade: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADES.map(g => (
                      <SelectItem key={g.value} value={g.value}>
                        <span className="flex items-center gap-2">
                          <Badge className={g.color + ' text-xs'}>{g.value}</Badge>
                          <span className="text-xs text-muted-foreground">{g.description}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Score de confiance: {formData.trust_score}</Label>
                <Slider
                  value={[formData.trust_score]}
                  onValueChange={([v]) => setFormData({ ...formData, trust_score: v })}
                  min={0}
                  max={100}
                  step={1}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0 (Non fiable)</span>
                  <span>100 (Très fiable)</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Note vendeur (0-5)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.seller_rating}
                    onChange={(e) => setFormData({ ...formData, seller_rating: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Note acheteur (0-5)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.buyer_rating}
                    onChange={(e) => setFormData({ ...formData, buyer_rating: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Raison de la modification *</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Expliquez pourquoi vous modifiez ce score (min 10 caractères)..."
                  rows={3}
                />
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-500">
                  Les modifications manuelles sont marquées et visibles. 
                  Le score ne sera pas recalculé automatiquement tant qu'il est marqué comme manuel.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleManualUpdate} disabled={loading}>
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={historyDialogOpen} onOpenChange={(open) => {
          setHistoryDialogOpen(open);
          if (open) loadHistory();
        }}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm">
              <History className="w-4 h-4 mr-1" />
              Historique
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Historique des modifications</DialogTitle>
              <DialogDescription>
                Modifications de score pour {user.first_name} {user.last_name}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto space-y-3">
              {scoreHistory.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune modification enregistrée
                </p>
              ) : (
                scoreHistory.map((log, i) => (
                  <div key={i} className="p-3 bg-muted/50 rounded-lg text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">
                        {log.action_type === 'score_manually_updated' ? 'Manuel' : 'Auto'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString('fr-FR')}
                      </span>
                    </div>
                    {log.details?.reason && (
                      <p className="text-muted-foreground mb-2">"{log.details.reason}"</p>
                    )}
                    {log.details?.old_values && (
                      <div className="text-xs space-y-1">
                        {Object.entries(log.details.old_values).map(([key, val]) => (
                          <div key={key} className="flex gap-2">
                            <span className="text-muted-foreground">{key}:</span>
                            <span className="line-through text-red-400">{val}</span>
                            <span>→</span>
                            <span className="text-green-400">{log.details.new_values?.[key]}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UserScoreManager;
